
import React, { useRef } from 'react';
import { Upload, FileText, CheckCircle2, XCircle } from 'lucide-react';

interface FileUploadProps {
  label: string;
  fileName: string | null;
  onFileSelect: (file: File) => void;
  error?: string;
}

const FileUpload: React.FC<FileUploadProps> = ({ label, fileName, onFileSelect, error }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => inputRef.current?.click();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onFileSelect(file);
    }
  };

  return (
    <div className="space-y-1.5">
      <label className="text-xs font-bold text-brand-muted uppercase tracking-wider ml-1">{label}</label>
      <div
        onClick={handleClick}
        className={`
          relative border-2 border-dashed rounded-2xl p-6 transition-all duration-300 cursor-pointer flex flex-col items-center justify-center gap-3
          ${fileName ? 'border-brand-primary/50 bg-brand-primary/5' : 'border-brand-border hover:border-brand-primary/30 hover:bg-white/5'}
          ${error ? 'border-red-500/50 bg-red-500/5' : ''}
        `}
      >
        <input
          type="file"
          ref={inputRef}
          onChange={handleChange}
          className="hidden"
          accept="image/jpeg,image/png,image/jpg"
        />

        {fileName ? (
          <>
            <div className="w-12 h-12 rounded-full bg-brand-primary/20 flex items-center justify-center text-brand-primary">
              <CheckCircle2 size={24} />
            </div>
            <div className="text-center">
              <p className="text-sm font-bold text-white truncate max-w-[200px]">{fileName}</p>
              <p className="text-[10px] text-brand-primary uppercase font-bold tracking-widest mt-1">File Attached</p>
            </div>
          </>
        ) : (
          <>
            <div className="w-12 h-12 rounded-full bg-brand-border/50 flex items-center justify-center text-brand-muted group-hover:text-brand-primary transition-colors">
              <Upload size={24} />
            </div>
            <div className="text-center">
              <p className="text-xs font-medium text-white">Tap to upload document</p>
              <p className="text-[9px] text-brand-muted uppercase tracking-tighter mt-1">Supports JPG, PNG (Max 5MB)</p>
            </div>
          </>
        )}
      </div>
      {error && <p className="text-[10px] text-red-400 font-medium ml-1 flex items-center gap-1">
        <XCircle size={10} /> {error}
      </p>}
    </div>
  );
};

export default FileUpload;
